import {
  UPDATE_URLS,
  DraftObjects,
  IndexName,
  IndexTypes,
  PUBLISH,
  EventKind,
} from "./types";
import { Machine, assign, sendParent } from "xstate";
import GalleryFlowObject from "../../model/GalleryFlowObject";
import { getStringId } from "../utils/getInfo";
import VideoFlowObject from "../../model/VideoFlowObject";
import { UploadResponse } from "../../model/Screens";
import { replacer } from "../../model/Action";

/**
 * @param url a URL
 * @returns if a url field is actually a URL
 */
export const skipIfHttp = (url: string | File | undefined) => {
  // If undefined, returns false
  // If url is a File, return false
  // If url is a string and http return true
  return url && typeof url === "string" && url.startsWith("http");
};

/**
 * Convert a base64 data URL to a File object
 */
const dataURLtoFile = (dataurl: string, filename: string): File => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// ASSUMPTION from all objects to be uploaded are `File`s.
/**
 * Ensure the flow exists on Treemo before uploading files.
 * For AI-created flows that haven't been saved yet, this creates the flow
 * on the server and patches window.flow_json with the new string_id.
 * Subsequent calls are a no-op once string_id is set.
 */
let _ensureFlowPromise: Promise<void> | null = null;
const ensureFlowCreated = (): Promise<void> => {
  if (getStringId()) return Promise.resolve();
  // Deduplicate: if already in progress, return the same promise
  if (_ensureFlowPromise) return _ensureFlowPromise;
  _ensureFlowPromise = (async () => {
    try {
      const flowJson = (window as any).flow_json;
      const title = flowJson?.flowboard?.flows?.[0]?.title || 'AI Generated Flow';

      // Note: Empty preview URLs are handled by the editor's canvas-based thumbnail generation.
      // We no longer apply external placeholder URLs here as they cause CORS issues.

      // Use replacer to strip internal fields like __ref before sending to Treemo
      const cleanedJson = JSON.parse(JSON.stringify(flowJson, replacer));
      const resp = await fetch('/ai/export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flow_json: cleanedJson, title }),
      });
      const result = await resp.json();
      if (result.success && result.string_id) {
        flowJson.flowboard.string_id = result.string_id;
        const flowMetadata = (window as any).flow_metadata;
        if (flowMetadata?.flowboard) {
          if (result.id) {
            flowMetadata.flowboard.id = result.id;
          }
          // Update metadata with full document info from Treemo
          // This is critical - without proper generic_file_ids, publish won't create the published JSON file
          if (result.document_json_draft) {
            flowMetadata.flowboard.document_json_draft = result.document_json_draft;
          }
          if (result.document_json_published) {
            flowMetadata.flowboard.document_json_published = result.document_json_published;
          }
          console.log('[ensureFlowCreated] Updated metadata from Treemo:', {
            draft_id: flowMetadata.flowboard.document_json_draft?.generic_file_id,
            published_id: flowMetadata.flowboard.document_json_published?.generic_file_id
          });
        }
        history.replaceState(null, '', `/flow/${result.string_id}/edit`);
        console.log('[ensureFlowCreated] Flow created on Treemo:', result.string_id);
      } else {
        throw new Error(result.error || 'Failed to create flow on server');
      }
    } catch (err) {
      console.error('[ensureFlowCreated] Error:', err);
      _ensureFlowPromise = null; // Allow retry on failure
      throw err;
    }
  })();
  return _ensureFlowPromise;
};

// If the `typeof` the externalURL is `string`, this object is already uploaded
/**
 * Traverse through all keys which may need to be uploaded. Upload entire list
 * **ASSUMPTION** from all objects to be uploaded are `File`s.
 *  If the `typeof` the externalURL is `string`, this object is already uploaded
 * @param draft a Draft object
 */
const uploadObject = async (draft: { obj: DraftObjects; screen: number }) => {
  await ensureFlowCreated();
  let mediaFiles: File[] = [];
  if (draft.obj.tag === "asset") {
    if (skipIfHttp(draft.obj.value.externalURL.value)) {
      console.info("skipping a image asset since it's http");
      return;
    }
    // If the type is undefined it's a screen preview not a mixedMediaObject
    let formData = new FormData();
    let fileToUpload = draft.obj.value.externalURL.value;

    // Convert base64 data URL to File if needed
    if (typeof fileToUpload === "string" && fileToUpload.startsWith("data:")) {
      console.info("Converting base64 preview screenshot to File for upload");
      fileToUpload = dataURLtoFile(fileToUpload, `preview-${Date.now()}.png`);
    }

    formData.append("mediaFile", fileToUpload);
    return fetch(`/flow/${getStringId()}/uploadFiles`, {
      method: "POST",
      body: formData,
    }).then((r) => r.json()).then((result) => [result]); // Wrap in array for consistency
  } else {
    let obj = draft.obj.value;

    // Skip text and shape objects - they don't need file uploads
    if (obj.type === "text" || obj.type === "shape") {
      console.log(`Skipping upload for ${obj.type} object - no files to upload`);
      return undefined;
    }

    // Handle originalImageAsset
    if (obj.originalImageAsset!.externalURL.__tag === "file" && !skipIfHttp(obj.originalImageAsset!.externalURL.value)) {
      let firstFile = obj.originalImageAsset!.externalURL.value;
      console.log(
        `We're uploading a wonderful original 1234567 image 🌉: `,
        firstFile
      );
      mediaFiles.push(firstFile);
    } else if (
      obj.originalImageAsset!.externalURL.__tag === "string" &&
      obj.originalImageAsset!.externalURL.value.startsWith("data:") &&
      !skipIfHttp(obj.originalImageAsset!.externalURL.value)
    ) {
      // Convert base64 to File and upload
      console.warn(`[UPLOAD BUG FIX] Found base64 in originalImageAsset.externalURL, converting to File for upload`);
      let file = dataURLtoFile(obj.originalImageAsset!.externalURL.value, `${obj.id}-original.png`);
      mediaFiles.push(file);
      // Update the draft to have File instead of base64
      obj.originalImageAsset!.externalURL = {
        __tag: "file",
        value: file
      };
    }

    // Handle croppedImageAsset
    if (obj.croppedImageAsset!.externalURL.__tag === "file" && !skipIfHttp(obj.croppedImageAsset!.externalURL.value)) {
      mediaFiles.push(obj.croppedImageAsset!.externalURL.value);
    } else if (
      obj.croppedImageAsset!.externalURL.__tag === "string" &&
      obj.croppedImageAsset!.externalURL.value.startsWith("data:") &&
      !skipIfHttp(obj.croppedImageAsset!.externalURL.value)
    ) {
      // Convert base64 to File and upload
      console.warn(`[UPLOAD BUG FIX] Found base64 in croppedImageAsset.externalURL, converting to File for upload`);
      let file = dataURLtoFile(obj.croppedImageAsset!.externalURL.value, `${obj.id}-cropped.png`);
      mediaFiles.push(file);
      // Update the draft to have File instead of base64
      obj.croppedImageAsset!.externalURL = {
        __tag: "file",
        value: file
      };
    }
    if (obj.type == "pdf") {
      console.log(
        `We're uploading a wonderful pdf 📁: `,
        obj.pdfAsset!.externalURL
      );
      if (
        obj.pdfAsset!.externalURL.__tag === "file" &&
        !skipIfHttp(obj.pdfAsset!.externalURL.value)
      ) {
        mediaFiles.push(obj.pdfAsset!.externalURL.value);
      } else if (
        obj.pdfAsset!.externalURL.__tag === "string" &&
        obj.pdfAsset!.externalURL.value.startsWith("data:") &&
        !skipIfHttp(obj.pdfAsset!.externalURL.value)
      ) {
        console.warn(`[UPLOAD BUG FIX] Found base64 in pdfAsset.externalURL, converting to File for upload`);
        let file = dataURLtoFile(obj.pdfAsset!.externalURL.value, `${obj.id}.pdf`);
        mediaFiles.push(file);
        obj.pdfAsset!.externalURL = {
          __tag: "file",
          value: file
        };
      }
    } else if (obj.type == "video") {
      if (
        obj.videoAsset!.externalURL.__tag === "file" &&
        (obj as VideoFlowObject).isyoutube == "NO"
      ) {
        mediaFiles.push(obj.videoAsset!.externalURL.value);
      } else if (
        obj.videoAsset!.externalURL.__tag === "string" &&
        obj.videoAsset!.externalURL.value.startsWith("data:") &&
        (obj as VideoFlowObject).isyoutube == "NO" &&
        !skipIfHttp(obj.videoAsset!.externalURL.value)
      ) {
        console.warn(`[UPLOAD BUG FIX] Found base64 in videoAsset.externalURL, converting to File for upload`);
        let file = dataURLtoFile(obj.videoAsset!.externalURL.value, `${obj.id}.mp4`);
        mediaFiles.push(file);
        obj.videoAsset!.externalURL = {
          __tag: "file",
          value: file
        };
      }
    } else if (obj.type == "gallery") {
      const galleryFiles = galleryHelper(obj as GalleryFlowObject);
      mediaFiles = [...mediaFiles, ...galleryFiles];
      console.debug(`We've got ${mediaFiles.length} total files to upload (including ${galleryFiles.length} gallery images)`);
    }
    if (mediaFiles.length > 0) {
      return uploadHelper(mediaFiles as File[]);
    } else {
      return new Promise((resolve) => {
        resolve([]);
      });
    }
  }
};

const galleryHelper = (obj: GalleryFlowObject) => {
  let mediaFiles: File[] = [];
  for (
    let index = 0;
    index < obj.galleryAssets!.fullSizedImages.length;
    index++
  ) {
    const element = obj.galleryAssets!.fullSizedImages[index];

    // Skip if it's already an HTTP URL
    if (element.externalURL.__tag === "string" && skipIfHttp(element.externalURL.value)) {
      console.debug("skipping already uploaded gallery image");
      continue;
    }

    // Handle File objects
    if (element.externalURL.__tag === "file") {
      let fileToAdd: File = element.externalURL.value;
      mediaFiles.push(fileToAdd);
    }
    // Handle base64 strings - convert to File
    else if (element.externalURL.__tag === "string" && element.externalURL.value.startsWith("data:")) {
      console.warn(`[UPLOAD BUG FIX] Found base64 in gallery image ${index}, converting to File for upload`);
      let file = dataURLtoFile(element.externalURL.value, `${obj.id}-gallery-${index}.png`);
      mediaFiles.push(file);
      // Update the element to have File instead of base64
      element.externalURL = {
        __tag: "file",
        value: file
      };
    }
  }
  console.debug(mediaFiles);
  return mediaFiles;
};

/**
 * Uploads a list of media files and returns it's url on rackspace
 * @param {File[]} mediaFiles
 */
const uploadHelper = async (mediaFiles: File[]) => {
  await ensureFlowCreated();
  console.log("testing 124");
  let responses = [];
  for (let i = 0; i < mediaFiles.length; i++) {
    let formData = new FormData();
    formData.append("mediaFile", mediaFiles[i]);
    responses.push(
      fetch(`/flow/${getStringId()}/uploadFiles`, {
        method: "POST",
        body: formData,
      }).then((r) => r.json())
    );
  }
  console.debug(responses);
  return Promise.all(responses);
};

interface UploadContext {
  draft: { obj: DraftObjects; screen: number };
  response?: UploadResponse[];
}

interface UploadSchema {
  states: { inprogress: {}; complete: {} };
}

/**
 * a xstate machine handling transitioning between:
 * - Objects in need of uploading
 * - Objects in need of being updated to use a URL media
 * - Notifying main machine of completion
 */
export const uploadMachine = Machine<UploadContext, UploadSchema>({
  id: "upload",
  initial: "inprogress",
  states: {
    inprogress: {
      //@ts-ignore
      invoke: {
        // All invoked services need a unique ID
        id: "uploadObject",
        // Accessing context/event in a service is not implicit.
        // src: "uploadObject", ⛔️
        // src:  (ctx: UploadContext, _: any) => uploadObject(ctx.draft), ✅
        src: (ctx: UploadContext, _: any) => uploadObject(ctx.draft),
        // Once `uploadObject`'s Promise resolves, we set the context's `uploadResponse` to the received result
        // We then transition to the `complete` state
        onDone: {
          target: "complete",
          actions: assign({
            response: (ctx: UploadContext, event) => {
              console.log(event);
              if (event.data) {
                // For assets, event.data is already wrapped in an array from uploadObject
                // For mmo, event.data is already an array from uploadHelper
                return event.data;
              }
            },
          }),
        },
        onError: () => console.error("AHH it failed"),
      },
    },
    complete: {
      // `sendParent` passes this backup to `flow` machine
      entry: sendParent((ctx: UploadContext, _) => {
        if (!ctx.response) {
          return { type: PUBLISH };
        }
        // eventKind may not exist on cloned objects (cloneDeep strips prototypes)
        const kind = typeof ctx.draft.obj.value.eventKind === 'function'
          ? ctx.draft.obj.value.eventKind()
          : (ctx.draft.obj.tag === 'asset' ? EventKind.Flow : EventKind.Mmo);
        return {
              type: UPDATE_URLS,
              screen: ctx.draft.screen,
              id: ctx.draft.obj.value.id,
              kind,
              uploadResponse: ctx.response!,
              indexMap: determineIndices(ctx.draft.obj),
            };
      }),
    },
  },
});

const determineIndices = (draft: DraftObjects) => {
  const indexMap = new Map<IndexName, number>();
  if (draft.tag === "asset") {
    if (skipIfHttp(draft.value.externalURL.value)) {
      return;
    }
    indexMap.set(IndexTypes.previewScreenShot, 0);
  } else {
    let obj = draft.value;
    if (obj.type == "image" || obj.type == "webview") {
      let counter = 0;
      if (!skipIfHttp(obj.originalImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.originalImageAsset, counter);
        counter += 1;
      }

      if (!skipIfHttp(obj.croppedImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.croppedImageAsset, counter);
      }
    } else if (obj.type == "pdf") {
      let counter = 0;
      if (!skipIfHttp(obj.originalImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.originalImageAsset, counter);
        counter += 1;
      }
      if (!skipIfHttp(obj.croppedImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.croppedImageAsset, counter);
        counter += 1;
      }
      if (!skipIfHttp(obj.pdfAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.pdfAsset, counter);
      }
    } else if (obj.type == "video") {
      let counter = 0;
      if (!skipIfHttp(obj.originalImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.originalImageAsset, counter);
        counter += 1;
      }
      if (!skipIfHttp(obj.croppedImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.croppedImageAsset, counter);
        counter += 1;
      }
      if (
        !skipIfHttp(obj.videoAsset!.externalURL.value) &&
        (obj as VideoFlowObject).isyoutube == "NO"
      ) {
        indexMap.set(IndexTypes.videoAsset, counter);
      }
    } else if (obj.type == "gallery") {
      let counter = 0;
      if (!skipIfHttp(obj.originalImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.originalImageAsset, counter);
        counter += 1;
      }
      if (!skipIfHttp(obj.croppedImageAsset!.externalURL.value)) {
        indexMap.set(IndexTypes.croppedImageAsset, counter);
        counter += 1;
      }
      for (
        let index = 0;
        index < obj.galleryAssets!.fullSizedImages.length;
        index++
      ) {
        const element = obj.galleryAssets!.fullSizedImages[index];
        if (!skipIfHttp(element.externalURL.value)) {
          indexMap.set(`galleryAsset-${element.id}`, counter);
          counter += 1;
        }
      }
    }
  }
  return indexMap;
};
